import { TileLayer } from 'leaflet';
import { uniqueId } from 'lodash';
import JSDCLayer from '../Layer/JSDCLayer';
import Event from '../utils/Event';
import ConfigProvider from './ConfigProvider';
import ApiProvider from './ApiProvider';
import { IArticleProxyParser } from './proxyParser/@types';

export type DguidewalksOptions = {
  layerNameOrder?: string[];
  config?: ConfigProvider;
  articleParser?: IArticleProxyParser;
};

export default class Dguidewalks {
  layerNameOrder: string[];
  gisDataLoadEvent = new Event();
  config?: ConfigProvider;
  api?: ApiProvider;
  articleProxyParser?: IArticleProxyParser;

  constructor(options: DguidewalksOptions) {
    this.layerNameOrder = options.layerNameOrder || [];
    this.config = options.config;
    this.api = options.config ? new ApiProvider(options.config) : undefined;
    this.articleProxyParser = options.articleParser;
  }

  async getSceneArticles() {
    if (!this.articleProxyParser) {
      console.warn('Article parser not configured');
      return [];
    }
    return await this.articleProxyParser.getAll();
  }

  async getSceneDetailArticleByTitle(
    title: string,
    fallbackUrl?: string | null,
  ) {
    if (!this.articleProxyParser) {
      throw new Error('Article parser not configured');
    }
    return await this.articleProxyParser.getDetailByTitle(title, fallbackUrl);
  }

  async loadGisData(): Promise<JSDCLayer[]> {
    // implement data loading
    const basemaps = [
      {
        name: '正射影像(混合)',
        url: 'https://wmts.nlsc.gov.tw/wmts/PHOTO_MIX/default/GoogleMapsCompatible/{z}/{y}/{x}',
      },
      {
        name: '臺灣通用正射影像',
        url: 'https://wmts.nlsc.gov.tw/wmts/PHOTO2/default/GoogleMapsCompatible/{z}/{y}/{x}.png',
      },
      {
        name: '臺灣通用電子地圖(灰階)',
        url: 'https://wmts.nlsc.gov.tw/wmts/EMAP01/default/GoogleMapsCompatible/{z}/{y}/{x}.png',
      },
      {
        name: '臺灣通用電子地圖(等高線+門牌)',
        url: 'https://wmts.nlsc.gov.tw/wmts/EMAP5/default/GoogleMapsCompatible/{z}/{y}/{x}.png',
      },
      {
        name: '臺灣堡圖（1904年）',
        url: 'https://gis.sinica.edu.tw/tileserver/file-exists.php?img=JM20K_1904-jpg-{z}-{x}-{y}',
      },
      {
        name: '縣市界',
        url: 'https://wmts.nlsc.gov.tw/wmts/CITY/default/GoogleMapsCompatible/{z}/{y}/{x}.png',
      },
      {
        name: '鄉鎮市區界',
        url: 'https://wmts.nlsc.gov.tw/wmts/TOWN/default/GoogleMapsCompatible/{z}/{y}/{x}',
      },
      {
        name: '村里界',
        url: 'https://wmts.nlsc.gov.tw/wmts/Village/default/GoogleMapsCompatible/{z}/{y}/{x}.png',
      },
      {
        name: '林班地',
        url: 'https://tile.happyman.idv.tw/map/forest/{z}/{x}/{y}.png',
      },
      {
        name: '地質敏感區',
        url: 'https://wmts.nlsc.gov.tw/wmts/GeoSensitive2/default/GoogleMapsCompatible/{z}/{y}/{x}.png',
      },
      {
        name: '地質敏感區(山崩與地滑)',
        url: 'https://wmts.nlsc.gov.tw/wmts/GeoSensitive/default/GoogleMapsCompatible/{z}/{y}/{x}.png',
      },
      {
        name: '公有土地地籍圖',
        url: 'https://wmts.nlsc.gov.tw/wmts/LAND_OPENDATA/default/GoogleMapsCompatible/{z}/{y}/{x}.png',
      },
      {
        name: '1921-日治地形圖-1:25,000',
        url: 'https://gis.sinica.edu.tw/tileserver/file-exists.php?img=JM25K_1921-png-{z}-{x}-{y}',
      },
      {
        name: 'OSM',
        url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
      },
      {
        name: '魯地圖',
        url: 'https://gis.sinica.edu.tw/ls/file-exists.php?img=rudy-png-{z}-{x}-{y}',
      },
      // {
      //   name: "1/5000像片基本圖65年",
      //   url: "https://wmts.nlsc.gov.tw/wmts/TOPO05KPHOTO_065/default/GoogleMapsCompatible/{z}/{y}/{x}.png",
      // },
      // {
      //   name: "1/5000像片基本圖66年",
      //   url: "https://wmts.nlsc.gov.tw/wmts/TOPO05KPHOTO_066/default/GoogleMapsCompatible/{z}/{y}/{x}.png",
      // },
      // {
      //   name: "1/5000像片基本圖67年",
      //   url: "https://wmts.nlsc.gov.tw/wmts/TOPO05KPHOTO_067/default/GoogleMapsCompatible/{z}/{y}/{x}.png",
      // },
      // {
      //   name: "1/5000像片基本圖68年",
      //   url: "https://wmts.nlsc.gov.tw/wmts/TOPO05KPHOTO_068/default/GoogleMapsCompatible/{z}/{y}/{x}.png",
      // },
      // {
      //   name: "1/5000像片基本圖69年",
      //   url: "https://wmts.nlsc.gov.tw/wmts/TOPO05KPHOTO_069/default/GoogleMapsCompatible/{z}/{y}/{x}.png",
      // },
      // {
      //   name: "1/5000像片基本圖70年",
      //   url: "https://wmts.nlsc.gov.tw/wmts/TOPO05KPHOTO_070/default/GoogleMapsCompatible/{z}/{y}/{x}.png",
      // },
    ];
    const result = basemaps.map(({ name, url }) => {
      const instance = new TileLayer(url, {
        maxZoom: 20,
      });
      const layer = new JSDCLayer({
        id: `${name}_${uniqueId('basemap')}`,
        description: {
          name: name,
          type: 'image',
        },
      });
      layer.setInstance(instance);
      return layer;
    });
    return result;
  }
}
